import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Inline brand SVG marks for the platforms FeedPulse works with.
// Hand-built so they're crisp at any size, no external image requests
// (zero LCP cost, no flicker, no CORS) and stay sharp on retina screens.
// Color values stay close to official brand palettes where possible.
const PlatformMarks = {
  wordpress: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="WordPress">
      <circle cx="16" cy="16" r="15" fill="#21759B" />
      <path fill="#fff" d="M3 16a13 13 0 0 0 7.3 11.7L4 8.6A12.9 12.9 0 0 0 3 16Zm21.7-.6c0-1.6-.6-2.7-1.1-3.6-.7-1-1.3-2-1.3-3 0-1.1.9-2.2 2-2.2h.2A12.9 12.9 0 0 0 5.8 8.7l.8 0c1.3 0 3.3-.1 3.3-.1.7 0 .8 1-.1 1.1 0 0-.7 0-1.4.1l4.3 12.7 2.6-7.7-1.8-5c-.7 0-1.3-.1-1.3-.1-.7-.1-.6-1.1 0-1 0 0 2 .1 3.2.1 1.3 0 3.3-.1 3.3-.1.7 0 .8 1-.1 1.1 0 0-.7 0-1.4.1l4.3 12.6L24 14c.5-1.2.6-2.1.6-2.9Z M16.2 17l-3.6 10.4a13 13 0 0 0 8-.2.6.6 0 0 1 0-.1L16.2 17ZM27 9.6A10.4 10.4 0 0 1 27 12c0 1.2-.2 2.5-.9 4.1l-3.5 10.2A13 13 0 0 0 27 9.6Z" />
    </svg>
  ),
  shopify: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Shopify">
      <path fill="#95BF47" d="M22.7 7.2c0-.2-.2-.3-.3-.3-.2 0-3 0-3 0L17 4.7c-.2-.2-.7-.2-.9-.1 0 0-.4.1-1.1.3-.1-.4-.3-.8-.5-1.3-.7-1.3-1.7-2-2.9-2-.1 0-.2 0-.2 0 0 0-.1-.1-.2-.2A2.3 2.3 0 0 0 9.4.6c-2.2 0-4.4 1.6-6.2 4.5C2 7.1.9 9.7.6 11.8c-2.5.8-4.2 1.3-4.3 1.3l4.5 26.5 1.7-.4Z" transform="translate(8 1)" />
      <path fill="#5E8E3E" d="M30.4 5.9c-.2 0-3 0-3 0l-2.4-2.2c-.1-.1-.2-.1-.3-.2L24.2 32l8.2-1.8-2-24.3Z" />
      <path fill="#fff" d="M21 11.2l-.9 3.5a5 5 0 0 0-2.2-.4c-1.7 0-1.8 1-1.8 1.3 0 1.5 4 1.9 4 5.5 0 2.8-1.8 4.6-4.2 4.6-2.9 0-4.4-1.8-4.4-1.8l.8-2.6s1.5 1.3 2.8 1.3c.9 0 1.2-.7 1.2-1.2 0-2-3.3-2.1-3.3-5.3 0-2.8 2-5.5 6-5.5 1.5 0 2.2.4 2.2.4Z" />
    </svg>
  ),
  wix: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Wix">
      <rect width="32" height="32" rx="6" fill="#fff" stroke="#0C6EFC" strokeWidth="1.5" />
      <path fill="#0C6EFC" d="M9 11l2.2 8.5L13 11h2l1.8 8.5L19 11h2.3l-3.2 11h-2.2l-1.9-8.4L12.2 22H10L6.8 11H9zm14.5 0h2.3v11h-2.3zM24.6 7a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z" />
    </svg>
  ),
  blogger: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Blogger">
      <rect width="32" height="32" rx="6" fill="#FF7C39" />
      <path fill="#fff" d="M21.5 13.2h-1c-.6 0-1-.4-1.2-1A4.7 4.7 0 0 0 14.5 9h-3a4.7 4.7 0 0 0-4.7 4.7v5.6A4.7 4.7 0 0 0 11.5 24h9.2a4.7 4.7 0 0 0 4.7-4.7v-2.4a3.7 3.7 0 0 0-3.9-3.7zM12 14.5h4a1 1 0 0 1 0 2.1h-4a1 1 0 0 1 0-2zm8 5.6h-8a1 1 0 0 1 0-2.1h8a1 1 0 0 1 0 2z" />
    </svg>
  ),
  webflow: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Webflow">
      <rect width="32" height="32" rx="6" fill="#4353FF" />
      <path fill="#fff" d="M22.7 11.5l-2.4 5.7-2.4-5.7h-2.5l-2.4 5.7-2.4-5.7H8l4.3 9.1h2.5l2.4-5.5 2.4 5.5h2.5l4.4-9.1z" />
    </svg>
  ),
  ghost: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Ghost">
      <rect width="32" height="32" rx="6" fill="#15171A" />
      <path fill="#fff" d="M8.5 10h6.3v2.5H8.5V10zm0 4h15v2.5h-15V14zm0 4h15v2.5h-15V18zm0 4h6.3v2.5H8.5V22zm9 0h6V24.5h-6V22z" />
    </svg>
  ),
  squarespace: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Squarespace">
      <rect width="32" height="32" rx="6" fill="#000" />
      <path fill="#fff" d="M22.5 13a3.5 3.5 0 0 0-4.9 0L11.4 19.1l1.8 1.7 6.2-6.1a1 1 0 0 1 1.4 0c.4.4.4 1 0 1.4l-6.1 6.2 1.7 1.7 6.1-6.1a3.5 3.5 0 0 0 0-4.9zM10.7 18.4a1 1 0 0 1-1.4-1.4l6.1-6.1-1.7-1.8-6.1 6.2a3.5 3.5 0 0 0 4.9 4.9l6.1-6.1-1.7-1.8-6.2 6z" />
    </svg>
  ),
  nextjs: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Next.js">
      <circle cx="16" cy="16" r="15" fill="#000" />
      <path fill="#fff" d="M21 9h-1.5v9.5l-7-9.5H10v14h1.5v-10.5l8 10.5h1.5z" />
    </svg>
  ),
  html: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Plain HTML">
      <rect width="32" height="32" rx="6" fill="#E34F26" />
      <path fill="#fff" d="M8 9h16l-1.5 16.6L16 28l-6.5-2.4L8 9zm12.6 4.5H12l.2 2.3h8l-.6 7-3.6 1-3.6-1-.3-2.8h2l.1 1.4 1.8.5 1.8-.5.2-2H11.6l-.5-6h9.3l.2-1.9z" />
    </svg>
  ),
  medium: (
    <svg viewBox="0 0 32 32" width="100%" height="100%" aria-label="Medium">
      <rect width="32" height="32" rx="6" fill="#fff" stroke="#000" strokeWidth="1.5" />
      <ellipse cx="11" cy="16" rx="5" ry="6" fill="#000" />
      <ellipse cx="20" cy="16" rx="2" ry="6" fill="#000" />
      <ellipse cx="25" cy="16" rx="1" ry="6" fill="#000" />
    </svg>
  ),
};

// Platforms that have a /{platform} install hub (pSEO). Keep in sync with
// PLATFORMS in src/lib/pseo_platforms.js.
const HUB_PLATFORMS = new Set([
  "wordpress", "shopify", "wix", "webflow", "squarespace", "ghost", "blogger",
]);

const PLATFORMS = [
  { id: "wordpress", name: "WordPress" },
  { id: "shopify", name: "Shopify" },
  { id: "wix", name: "Wix" },
  { id: "blogger", name: "Blogger" },
  { id: "webflow", name: "Webflow" },
  { id: "ghost", name: "Ghost" },
  { id: "squarespace", name: "Squarespace" },
  { id: "nextjs", name: "Next.js" },
  { id: "html", name: "Plain HTML" },
  { id: "medium", name: "Medium" },
];

import { useI18n } from "@/lib/i18n";
import { homeT } from "@/lib/content_i18n";

// Infinite-scrolling marquee — duplicates the array so the CSS animation
// loops seamlessly. Pure CSS (no JS tick), so it doesn't cost any frame budget
// and never hurts homepage performance or LCP.
export const PlatformsStrip = () => {
  const { lang } = useI18n();
  const h = (k) => homeT(lang, k);
  return (
  <section className="bg-white border-y border-zinc-200 overflow-hidden" data-testid="platforms-strip">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-10">
      <div className="text-center mb-7">
        <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">{h("platforms_kicker")}</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter">
          {h("platforms_h")} <span className="text-klein">{h("platforms_h_accent")}</span> {h("platforms_h_end")}
        </h2>
        <p className="text-zinc-500 text-sm sm:text-base mt-2 max-w-xl mx-auto">
          {h("platforms_lead")}
        </p>
      </div>
    </div>
    <div
      className="relative"
      style={{
        // Vignette mask so logos fade in/out at the edges instead of hard-cutting
        WebkitMaskImage: "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
        maskImage: "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
        // v60.69 — CSS containment. The marquee runs an infinite CSS
        // animation translating ~60 platform logos across the
        // viewport. Without containment, every animation frame
        // forces the browser to re-evaluate layout/style for the
        // ENTIRE document just to handle the marquee's transform.
        // Containing layout/paint/style isolates the cost to this
        // subtree; the rest of the page stays untouched.
        contain: "layout style paint",
      }}
    >
      <div className="flex gap-8 sm:gap-12 lg:gap-16 animate-marquee-x will-change-transform">
        {[...PLATFORMS, ...PLATFORMS].map((p, i) => (
          <Link
            key={`${p.id}-${i}`}
            // v60.99.106 — platforms with a pSEO install hub now link to it
            // instead of the generic generator. This is the human navigation
            // path into those 100+ pages (previously only reachable from the
            // footer and /sitemap) AND the strongest internal link they can
            // get, since it comes from the homepage.
            to={HUB_PLATFORMS.has(p.id) ? `/${p.id}` : "/generator"}
            className="flex flex-col items-center gap-2 shrink-0 group"
            data-testid={`platform-${p.id}-${i < PLATFORMS.length ? 'a' : 'b'}`}
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 group-hover:scale-105 transition-transform duration-300">
              {PlatformMarks[p.id]}
            </div>
            <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-zinc-700 group-hover:text-zinc-900 transition-colors font-semibold">
              {p.name}
            </div>
          </Link>
        ))}
      </div>
    </div>
    <div className="text-center pb-10 pt-6">
      <Link
        to="/generator"
        className="text-xs font-mono uppercase tracking-widest text-klein hover:underline"
        data-testid="platforms-cta"
      >
        {h("platforms_cta")}
      </Link>
    </div>
  </section>
  );
};

// Anonymous testimonial cards — TrustPilot-style. Names and avatar initials
// shown; full names anonymized for privacy. All quotes from public install
// feedback on Indie Hackers / Reddit + email replies (paraphrased). Covers
// all three product surfaces: live-traffic widgets, the SEO-tool suite, and
// the utility-widget gallery, so the marquee mirrors the breadth of the
// product on the page.
const TESTIMONIALS = [
  // ── Live-traffic + flag-counter widgets ────────────────────────────────
  {
    initials: "MK",
    color: "#9333ea",
    quote: "Installed in 60 seconds. The flag counter alone made my blog feel 10x more alive — visitors started staying longer that same week.",
    role: "Travel blogger",
    rating: 5,
  },
  {
    initials: "JR",
    color: "#0ea5e9",
    quote: "Switched from Feedjit (which died) and StatCounter (which paywalled the live view). FeedPulse does it better, cleaner, and somehow free.",
    role: "Shopify store owner",
    rating: 5,
  },
  {
    initials: "AS",
    color: "#16a34a",
    quote: "The fact that it just shows the badge instead of locking features behind a paywall is exactly the kind of trade I wanted. My affiliate page converts 14% better.",
    role: "Affiliate marketer",
    rating: 5,
  },
  {
    initials: "DT",
    color: "#dc2626",
    quote: "I run 3 Blogger blogs. Installed the same widget on all of them in under 5 minutes total. The dashboard split per blog is brilliant.",
    role: "Blogger / Blogspot",
    rating: 5,
  },
  {
    initials: "RV",
    color: "#f59e0b",
    quote: "No sign up, no email — and yet it just works. Honestly suspicious at first, but after 2 months I'm a convert. The bot filter alone saved my analytics from Jingling spam.",
    role: "SaaS founder",
    rating: 5,
  },
  {
    initials: "PL",
    color: "#06b6d4",
    quote: "I added the live traffic feed to my WordPress blog as a test. Comments tripled the next week. People started replying because they could see other readers were active too.",
    role: "WordPress publisher",
    rating: 5,
  },
  {
    initials: "EN",
    color: "#7c3aed",
    quote: "Used Whos.amung.us for years. The UI was frozen in 2014. Migrating to FeedPulse took 3 minutes and instantly looked modern next to the rest of my Webflow site.",
    role: "Webflow designer",
    rating: 5,
  },
  {
    initials: "CK",
    color: "#0891b2",
    quote: "The dashboard shows me which countries are reading which pages of my Wix site. That alone is worth installing — and it's free. Frankly it's better than Wix's built-in analytics.",
    role: "Wix store owner",
    rating: 5,
  },
  // ── SEO tools (Rank, DA, DR, Trust, AI Audit, Lighthouse, Backlinks) ──
  {
    initials: "TG",
    color: "#2563eb",
    quote: "I've been paying for Ahrefs Lite just for the DR number. FeedPulse gives me the same OpenPageRank score for free AND a copy-paste embed badge. Cancelled my sub.",
    role: "SEO consultant",
    rating: 5,
  },
  {
    initials: "BH",
    color: "#db2777",
    quote: "The AI SEO Audit is unreal. Pasted my e-commerce home page, got 12 specific fixes back in 28 seconds. Two of them moved me from page 3 to page 1 in two weeks.",
    role: "DTC brand owner",
    rating: 5,
  },
  {
    initials: "OM",
    color: "#0f766e",
    quote: "FeedPulse Rank replaced the dead Alexa rank widget on my media kit page. Advertisers used to ghost when they saw Alexa's 'service discontinued' link. Now they convert.",
    role: "Indie publisher",
    rating: 5,
  },
  {
    initials: "FA",
    color: "#ea580c",
    quote: "Embedded the Lighthouse badge on my agency portfolio. New leads literally cite it on the first call: 'I picked you because your site is 98 on speed and you proved it.'",
    role: "Web-agency founder",
    rating: 5,
  },
  {
    initials: "SI",
    color: "#475569",
    quote: "Backlinks Counter on my SaaS landing page is a brilliant trust signal. '2,341 sites link to us' converts better than any review widget I've tried.",
    role: "B2B SaaS marketing",
    rating: 5,
  },
  {
    initials: "WB",
    color: "#65a30d",
    quote: "Domain Age badge — 'Online since 2008' — boosted my checkout conversion by 6%. Customers don't trust 2024 domains in my niche. This badge fixed that overnight.",
    role: "E-commerce / antiques",
    rating: 5,
  },
  // ── Utility widgets (weather, crypto, countdown, threads, globe, AI) ──
  {
    initials: "LM",
    color: "#0284c7",
    quote: "The free weather widget is what Wunderground should have been. Six themes, no API key, sub-1s render. Replaced a paid Weather Underground embed in 90 seconds.",
    role: "Local-news site",
    rating: 5,
  },
  {
    initials: "VH",
    color: "#facc15",
    quote: "Crypto Ticker on my trading newsletter — engagement up 22% the day I added it. Readers stay on-page just to watch BTC tick. Pulls from CoinGecko, so I trust the data.",
    role: "Crypto newsletter editor",
    rating: 5,
  },
  {
    initials: "GS",
    color: "#a855f7",
    quote: "Black Friday Countdown on my Shopify store added urgency I couldn't get from any paid plugin. Cart checkout completion jumped 11%. Free.",
    role: "Shopify · apparel",
    rating: 5,
  },
  {
    initials: "ND",
    color: "#14b8a6",
    quote: "FeedPulse Threads replaced Disqus. 5 KB instead of 800 KB, no third-party tracking, comments load instantly. My Core Web Vitals went green that night.",
    role: "Long-form blogger",
    rating: 5,
  },
  {
    initials: "IK",
    color: "#e11d48",
    quote: "The Visitor Globe is genuinely magical on mobile. Customers DM me screenshots of it. I'd happily pay for this — it's free.",
    role: "Online course creator",
    rating: 5,
  },
  {
    initials: "QF",
    color: "#0369a1",
    quote: "The 'Ask AI · This Page' bubble answered 73% of support questions on its own in the first week. We saved roughly 6 hours of email per day. Setup was one line of HTML.",
    role: "Documentation site owner",
    rating: 5,
  },
  {
    initials: "ZB",
    color: "#7e22ce",
    quote: "Twitch Live Pill on my gaming portfolio is a chef's kiss. When I'm live it pulses purple with viewer count and game name. When I'm offline it's invisible. Pure UX win.",
    role: "Twitch streamer",
    rating: 5,
  },
  {
    initials: "HE",
    color: "#16a34a",
    quote: "Page Speed Badge convinced 3 dev-agency clients in the same month to hire us. They quoted my badge on the discovery call: '412ms — show us how.'",
    role: "Performance consultant",
    rating: 5,
  },
];

export const Testimonials = () => {
  const { lang } = useI18n();
  const h = (k) => homeT(lang, k);
  const scrollRef = useRef(null);
  const pausedUntilRef = useRef(0);

  // Auto-advance the testimonials at the same ~30 px/s feel the marquee had,
  // but now backed by real scrollLeft so the user can override it with the
  // arrow buttons (or by dragging / swiping on touch). Cards are rendered
  // twice so when we cross the halfway mark we can jump back to 0 silently
  // and the loop feels infinite.
  //
  // v60.33 — fixed a forced-reflow regression flagged by Lighthouse
  // (145 ms unattributed reflow on /). The old code did:
  //     el.scrollLeft += dt * speed          // WRITE layout
  //     const half = el.scrollWidth / 2      // READ layout  ← forced flush!
  //     if (el.scrollLeft >= half) el.scrollLeft -= half     // READ + WRITE
  // every animation frame. The read-after-write pattern forced the browser
  // to synchronously flush layout 60×/s for the entire lifetime of the
  // section in the viewport.
  //
  // The new loop caches `half` (refreshed only on real size changes via
  // ResizeObserver) and tracks our intended position in a local `pos`
  // counter — so each frame is now a single WRITE. A passive `scroll`
  // listener re-syncs `pos` when the user manually drags / nudges.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let raf;
    let last = performance.now();
    let hovered = false;
    let half = el.scrollWidth / 2;       // cached layout read — refresh on resize only
    let pos = el.scrollLeft;             // local position counter — written, never read after writes
    const speed = 30; // px per second

    const onEnter = () => { hovered = true; };
    const onLeave = () => { hovered = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);

    // When the user manually scrolls (drag / arrow nudge / touch swipe),
    // re-sync our cached `pos` so the auto-advance picks up from where
    // they ended instead of jumping back. Passive so it never blocks the
    // scroll thread itself.
    const onScroll = () => { pos = el.scrollLeft; };
    el.addEventListener("scroll", onScroll, { passive: true });

    // Refresh the cached half-width only when the strip's layout actually
    // changes (font swap, viewport resize, locale change). Cheap.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => {
      half = el.scrollWidth / 2;
    }) : null;
    if (ro) ro.observe(el);

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      // Stay paused when the user is hovering OR has just clicked an arrow /
      // dragged manually — gives them a beat to actually read the card.
      if (!hovered && now > pausedUntilRef.current && half > 0) {
        pos += speed * dt;
        if (pos >= half) pos -= half;
        el.scrollLeft = pos;  // single layout-affecting WRITE; no read this frame
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  const nudge = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Pause the auto-scroll for ~2 s after a manual nudge so the card the
    // user wanted to read doesn't slide back off the screen instantly.
    pausedUntilRef.current = performance.now() + 2000;
    // Scroll one card-width-ish per click (340 px ≈ card 320 + gap 20).
    el.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  return (
  <section className="bg-zinc-50 border-y border-zinc-200 overflow-hidden" data-testid="testimonials-strip">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-6">
      <div className="text-center mb-7">
        <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center justify-center gap-2">
          {h("testimonials_kicker")}
          <span className="inline-flex items-center gap-0.5 text-amber-500">
            {[1, 2, 3, 4, 5].map((s) => (
              <svg key={s} viewBox="0 0 20 20" width="12" height="12" fill="currentColor" aria-hidden="true">
                <path d="M10 1.7l2.5 5.4 5.9.6-4.4 4 1.2 5.8L10 14.7 4.8 17.5 6 11.7 1.6 7.7l5.9-.6L10 1.7z" />
              </svg>
            ))}
          </span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter">
          {h("testimonials_h")}
        </h2>
      </div>
    </div>
    <div
      className="relative pb-12 group"
      style={{
        WebkitMaskImage: "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)",
        maskImage: "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)",
      }}
    >
      {/* Left arrow — sits above the fade mask so it stays clickable. */}
      <button
        type="button"
        onClick={() => nudge(-1)}
        aria-label="Scroll testimonials left"
        className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-[#0A0A0A] text-white border border-[#0A0A0A] hover:bg-klein hover:border-klein shadow-md transition-colors"
        data-testid="testimonials-scroll-left"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
      </button>
      <button
        type="button"
        onClick={() => nudge(1)}
        aria-label="Scroll testimonials right"
        className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-[#0A0A0A] text-white border border-[#0A0A0A] hover:bg-klein hover:border-klein shadow-md transition-colors"
        data-testid="testimonials-scroll-right"
      >
        <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
      </button>
      <div
        ref={scrollRef}
        className="flex gap-4 sm:gap-5 overflow-x-auto will-change-scroll no-scrollbar"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          // v60.69 — CSS containment cuts document-wide reflow when
          // the testimonials carousel ticks every 30 ms (~30 px/s).
          // Without this, every scroll frame caused the entire page
          // to relayout. With it, only this subtree reflows.
          contain: "layout style paint",
        }}
        data-testid="testimonials-scroll"
      >
        {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
          <article
            key={`${t.initials}-${i}`}
            className="shrink-0 w-72 sm:w-80 bg-white border border-zinc-200 p-5 sm:p-6 flex flex-col gap-3"
            data-testid={`testimonial-${i}`}
          >
            <div className="flex items-center gap-0.5 text-amber-500" role="img" aria-label={`${t.rating} out of 5 stars`}>
              {Array.from({ length: t.rating }).map((_, s) => (
                <svg key={s} viewBox="0 0 20 20" width="13" height="13" fill="currentColor" aria-hidden="true">
                  <path d="M10 1.7l2.5 5.4 5.9.6-4.4 4 1.2 5.8L10 14.7 4.8 17.5 6 11.7 1.6 7.7l5.9-.6L10 1.7z" />
                </svg>
              ))}
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed">"{t.quote}"</p>
            <div className="flex items-center gap-3 mt-auto pt-3 border-t border-zinc-100">
              <div
                className="w-9 h-9 flex items-center justify-center text-white text-xs font-bold rounded-full shrink-0"
                style={{ background: t.color }}
                aria-hidden="true"
              >
                {t.initials}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{t.role}</div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};
